<script setup lang="ts">
/**
 * The household home screen (SPEC §6.11, §8.6, §9.2).
 *
 * The default returning-user experience, and the screen a text message lands
 * on. It answers one question — what is the next meal and what can I do about
 * it — so the answer is above everything else, the two cards that follow are
 * things to do rather than things to read, and configuration is somewhere
 * else entirely.
 *
 * Nothing here navigates to an organizer screen. A family surface that offers
 * one is a family surface somebody taps into and gets refused by.
 */
import { computed, ref } from 'vue'
import ContributionList from '@/components/ContributionList.vue'
import PatientLoader from '@/components/PatientLoader.vue'
import RsvpCard from '@/components/RsvpCard.vue'
import {
  ApiError,
  endpoints,
  type AttendanceSummaryResponse,
  type FamilyMealResponse,
  type FamilyRsvpResponse,
} from '@/api'
import { asCount } from '@/utils/numbers'
import { describeWhen } from '@/utils/dates'

const meal = ref<FamilyMealResponse | null>(null)
const attendance = ref<AttendanceSummaryResponse | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

/**
 * Both halves of the date, always (SPEC §9.4).
 *
 * A link from a text may be opened days after it arrived, and "this Sunday" on
 * its own is then wrong in a way the reader cannot catch.
 */
const when = computed(() =>
  meal.value ? describeWhen(meal.value.localDate, meal.value.localTime) : null,
)

const where = computed(() => {
  if (!meal.value?.locationName) return null

  return meal.value.locationAddress
    ? `${meal.value.locationName} · ${meal.value.locationAddress}`
    : meal.value.locationName
})

/**
 * How many are coming, in the family's words (SPEC §9.2).
 *
 * Counts only, never names — which is what makes it the same sentence under
 * both household visibility settings (SPEC §6.31.1).
 */
const coming = computed(() => {
  const summary = attendance.value

  if (!summary) return null

  const people = asCount(summary.people)

  if (people === 0) return null

  const adults = asCount(summary.adults)
  const children = asCount(summary.children)

  return {
    headline: people === 1 ? '1 person coming' : `${people} people coming`,
    detail: [
      adults > 0 ? `${adults} ${adults === 1 ? 'adult' : 'adults'}` : null,
      children > 0 ? `${children} ${children === 1 ? 'kid' : 'kids'}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
  }
})

function describe(caught: unknown): string {
  return (
    (caught instanceof ApiError && caught.problem?.title) ||
    'We could not load your next meal just then. Try again in a moment.'
  )
}

async function load() {
  loading.value = true
  error.value = null

  try {
    const home = await endpoints.familyHome()

    meal.value = home.next ?? null
    attendance.value = home.attendance ?? null
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

/**
 * Takes the answer the card just saved.
 *
 * The totals move with it, because the family's own reply is part of them and
 * a headline that still said 16 after they answered would look broken. Counted
 * from the change rather than re-fetched: a round trip for arithmetic the
 * screen already has is a round trip somebody watches.
 */
function adopt(rsvp: FamilyRsvpResponse) {
  const previous = meal.value?.rsvp

  if (meal.value) {
    meal.value = { ...meal.value, rsvp }
  }

  if (!attendance.value || !previous) return

  const wasYes = previous.status === 'Yes'
  const isYes = rsvp.status === 'Yes'

  const adults = asCount(attendance.value.adults) - (wasYes ? asCount(previous.adultCount) : 0)
  const children = asCount(attendance.value.children) - (wasYes ? asCount(previous.childCount) : 0)

  attendance.value = {
    ...attendance.value,
    yes: asCount(attendance.value.yes) - (wasYes ? 1 : 0) + (isYes ? 1 : 0),
    adults: adults + (isYes ? asCount(rsvp.adultCount) : 0),
    children: children + (isYes ? asCount(rsvp.childCount) : 0),
    people: adults + children + (isYes ? asCount(rsvp.adultCount) + asCount(rsvp.childCount) : 0),
  }
}

void load()
</script>

<template>
  <div class="home">
    <v-alert v-if="error" type="error" variant="tonal">{{ error }}</v-alert>

    <PatientLoader v-if="loading" label="your next meal" @retry="load" />

    <!--
      Nothing scheduled is an ordinary state, not a failure. A group between
      series has no next meal, and saying so plainly beats an empty card.
    -->
    <section v-else-if="!meal" class="home__empty" data-testid="no-meal">
      <h1 class="home__title">Nothing on the table yet</h1>
      <p class="home__empty-text">
        There is no meal scheduled right now. Whoever organizes your meals will let you know when
        there is, and it will show up here.
      </p>
    </section>

    <template v-else>
      <header class="home__head">
        <!--
          Relative and absolute, in that order: the relative half is what
          somebody reads at a glance, the absolute half is what stops them
          being wrong about it (SPEC §9.4).
        -->
        <p v-if="when" class="home__when" data-testid="when">
          <strong v-if="when.relative">{{ when.relative }}</strong>
          <template v-if="when.relative"> · </template>{{ when.absolute }} · {{ when.time }}
        </p>

        <h1 class="home__title">{{ meal.title }}</h1>

        <p v-if="where" class="home__where">{{ where }}</p>
      </header>

      <!-- Above the fold, and the first thing a thumb reaches (SPEC §6.11). -->
      <section class="home__rsvp">
        <h2 class="home__heading">Who's coming?</h2>

        <RsvpCard
          :event-id="meal.id"
          :rsvp="meal.rsvp"
          :default-total="asCount(meal.defaultTotal)"
          :open="meal.isOpen"
          @saved="adopt"
        />
      </section>

      <ContributionList
        v-if="(meal.menu?.length ?? 0) > 0"
        compact
        :menu="meal.menu"
        :open="meal.isOpen"
        @changed="load"
      />

      <p v-if="coming" class="home__coming" data-testid="attendance">
        <span class="home__coming-headline">{{ coming.headline }}</span>
        <span v-if="coming.detail" class="home__coming-detail">{{ coming.detail }}</span>
      </p>

      <!--
        The way to everything else about this meal: the whole menu, the notes,
        and what it says when it is called off. Last, because it is a thing to
        read and the rest of the screen is things to do.
      -->
      <RouterLink class="home__more" :to="{ name: 'family-event', params: { eventId: meal.id } }">
        See the whole meal
      </RouterLink>
    </template>

    <!--
      Outside the meal block on purpose, so it is reachable when there is no
      next meal. A family between series is exactly who wants to look back at
      what they brought last time (MT-105).
    -->
    <RouterLink class="home__more" :to="{ name: 'family-history' }" data-testid="your-meals">
      Your meals
    </RouterLink>
  </div>
</template>

<style scoped>
.home {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.home__head {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.home__when {
  margin: 0;
  font-size: 1.05rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: rgb(var(--v-theme-primary));
}

.home__title {
  font-size: 1.75rem;
  line-height: 1.2;
  margin: 0;
}

.home__where {
  margin: 0;
  color: rgb(var(--v-theme-on-surface-variant));
}

.home__heading {
  font-size: 1.125rem;
  margin: 0 0 0.5rem;
}

.home__rsvp {
  display: flex;
  flex-direction: column;
}

.home__coming {
  display: flex;
  flex-direction: column;
  margin: 0;
}

.home__coming-headline {
  font-size: 1.05rem;
}

.home__coming-detail {
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface-variant));
}

/* Thumb-sized, like every other target on the family surface (SPEC §9.5). */
.home__more {
  align-self: flex-start;
  min-height: 2.75rem;
  display: inline-flex;
  align-items: center;
  color: rgb(var(--v-theme-primary));
}

.home__empty {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.home__empty-text {
  margin: 0;
  color: rgb(var(--v-theme-on-surface-variant));
}
</style>
